import { DateTime } from "luxon";
import { type ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useApplicationInsights } from "@packsize/application-monitoring";
import { packsizeHooks } from "@packsize/utils";

import { getUnmanagedTokenKey, setUnmanagedTokenPrefix, decodeJwtToken, isPacksizeUser } from "../../utils";

import { TokenContext } from "./token.context";

import "../../polyfills/abort-signal";

const VALIDATION_TIMEOUT = 1000 * 10;

const LOG_PREFIX = "[@packsize/authentication | TokenProvider]";

const checkToken = async (
	identityBaseRoute: string,
	token: string,
	validationTimeout: number = VALIDATION_TIMEOUT,
	abortSignal?: AbortSignal,
): Promise<boolean> => {
	const signals = [AbortSignal.timeout(validationTimeout)];

	if (abortSignal) {
		signals.push(abortSignal);
	}

	try {
		const response = await fetch(`${identityBaseRoute}/v1/oauth/token/validate`, {
			method: "POST",
			headers: {
				Authorization: `BEARER ${token}`,
			},
			signal: AbortSignal.any(signals),
		});

		return response.ok;
	} catch {
		return false;
	}
};

interface TokenProviderProps {
	children: ReactNode;
	identityBaseRoute: string;
	storagePrefix: string;
	validationTimeout?: number;
}

/**
 * @param children - The children to render.
 * @param storagePrefix - The prefix to use for the token storage key.
 * @param identityBaseRoute - The base route for the identity API.
 * @param validationTimeout - The timeout for the token validation call in ms. Defaults to 10000ms. Should not be changed unless absolutely necessary mainly used for testing.
 */
export const TokenProvider: React.FC<TokenProviderProps> = ({
	children,
	storagePrefix,
	identityBaseRoute,
	validationTimeout,
}) => {
	const applicationInsights = useApplicationInsights();

	setUnmanagedTokenPrefix(storagePrefix);

	const { value: token, set: setToken, remove: removeToken } = packsizeHooks.useRawLocalStorage(getUnmanagedTokenKey());

	const [tokenValid, setTokenValid] = useState<boolean | undefined>(undefined);

	const decodedToken = useMemo(() => decodeJwtToken(token), [token]);

	const bearerToken = useMemo(() => {
		if (token === null) {
			return null;
		}

		return `BEARER ${token}`;
	}, [token]);

	const validateToken = useCallback(
		async (signal?: AbortSignal) => {
			if (token === null) {
				setTokenValid(false);
				return;
			}

			const valid = await checkToken(identityBaseRoute, token, validationTimeout, signal);

			if (signal?.aborted) {
				return;
			}

			setTokenValid(valid);

			if (!valid) {
				removeToken();
			}
		},
		[token, removeToken, identityBaseRoute, validationTimeout],
	);

	const clearToken = useCallback(() => {
		removeToken();
		setTokenValid(false);
	}, [removeToken]);

	const processToken = useCallback(
		async (newToken: string): Promise<boolean> => {
			const valid = await checkToken(identityBaseRoute, newToken, validationTimeout);

			if (valid) {
				setToken(newToken);
			}

			return valid;
		},
		[identityBaseRoute, setToken, validationTimeout],
	);

	useEffect(() => {
		if (token === null) {
			setTokenValid(false);
			return;
		}

		const abortController = new AbortController();
		const signal = abortController.signal;

		validateToken(signal);

		return () => {
			abortController.abort();
		};
	}, [token, validateToken]);

	useEffect(() => {
		if (!decodedToken) {
			return;
		}

		if (!decodedToken.exp) {
			removeToken();
			return;
		}

		const expiryTime = DateTime.fromSeconds(decodedToken.exp);
		const timeToExpiryInMs = expiryTime.diffNow().toMillis();

		if (timeToExpiryInMs <= 0) {
			removeToken();
			return;
		}

		const timeout = setTimeout(() => removeToken(), timeToExpiryInMs);

		return () => {
			clearTimeout(timeout);
		};
	}, [removeToken, decodedToken]);

	useEffect(() => {
		if (decodedToken === null || decodedToken === undefined) {
			console.debug(LOG_PREFIX, "Skipping telemetry initializer registration — no current user");
			return;
		}

		console.debug(LOG_PREFIX, "Registering telemetry initializer", {
			userId: decodedToken.id,
			userName: decodedToken.sub,
			tenantId: decodedToken.tid,
			tenantName: decodedToken.tname,
			companyId: decodedToken.cid,
			companyName: decodedToken.cname,
		});

		const handler = applicationInsights.addTelemetryInitializer((envelope) => {
			envelope.tags ??= {};

			envelope.tags["ai.user.id"] = decodedToken.id;
			envelope.tags["ai.user.accountId"] = decodedToken.tid;

			envelope.data ??= {};

			envelope.data["userId"] = decodedToken.id;
			envelope.data["tenantId"] = decodedToken.tid;
			envelope.data["tenantName"] = decodedToken.tname;
			envelope.data["companyId"] = decodedToken.cid;
			envelope.data["companyName"] = decodedToken.cname;
			envelope.data["isPacksizeUser"] = isPacksizeUser(decodedToken.role);
		});

		return () => {
			console.debug(LOG_PREFIX, "Removing telemetry initializer", { userId: decodedToken.id });
			handler.remove();
		};
	}, [applicationInsights, decodedToken]);

	const value = useMemo(
		() => ({
			token,
			bearerToken,
			decodedToken,
			tokenValid,
			setToken,
			processToken,
			clearToken,
		}),
		[token, bearerToken, decodedToken, tokenValid, setToken, processToken, clearToken],
	);

	return <TokenContext.Provider value={value}>{children}</TokenContext.Provider>;
};
